import {
  MessageCircle, CalendarClock, MapPin,
  Share2, Bot, CalendarDays, Inbox, AtSign,
  QrCode, FileText, BarChart3,
  ShieldCheck, RefreshCw, ImagePlus, Filter,
  Sparkles, Users, UtensilsCrossed, Link2, LayoutList, Palette,
  Receipt, CreditCard, Search, Send,
  ClipboardCheck, SlidersHorizontal, Swords, KeyRound, MessagesSquare,
  Layers, CalendarRange, Images, Plug, Building2, ListChecks, Mail,
  Contact, Gauge, TrendingUp, Megaphone, CalendarCheck, BellRing, Video,
  Phone, PhoneIncoming, PhoneOutgoing,
} from "lucide-react";
import { FaGoogle, FaFacebookF, FaInstagram, FaWhatsapp, FaWordpress } from "react-icons/fa";

export const FEATURE_CATEGORIES = [
  {
    id: "gmb",
    slug: "google-business-profile-automation-white-label-software-for-agencies",
    keyword: "Google Business Profile automation",
    seoTitle: "White-Label Google Business Profile Software for Agencies",
    seoDescription:
      "Automate Google Business Profile for every client: AI review replies, post scheduling, audits and rank tracking - white-label, under your agency's brand.",
    group: "automation",
    tag: "Google Business Profile",
    title: "GMB Automation",
    image: "/googleBusinessProfile_autoomation_magicbolt.png",
    // Animated card art for the /features grid (FeaturesPage); `image` stays
    // the still used on detail pages, other grids and social previews.
    cardImage: "/google-profile-growth.webp",
    intro:
      "Connect a Google profile once, then audit, optimise, reply, post and track rankings - all from one dashboard.",
    accent: { Icon: FaGoogle, color: "#4285F4", tint: "rgba(66,133,244,0.10)" },
    points: [
      { icon: ClipboardCheck, title: "Profile Audit Report", desc: "A points-based score across info, attributes, photos, posts and reviews - tuned to restaurants, retail or services - with a prioritised action plan and a branded PDF download." },
      { icon: SlidersHorizontal, title: "One-Screen Optimization", desc: "Edit hours, description, links, social profiles, WhatsApp, attributes and photos, and publish each section straight to Google with a live profile-strength meter." },
      { icon: MessageCircle, title: "AI Review Replies", desc: "Every review in one inbox. AI replies in your brand voice with per-star templates - send as drafts for approval or fully automatic." },
      { icon: CalendarClock, title: "Posts & Media Scheduling", desc: "Updates, events, offers and alerts with call-to-action buttons, plus category-tagged photos and videos - post now or schedule for later." },
      { icon: MapPin, title: "Geo-Grid Rank Tracking", desc: "Track where you rank on Google Maps for any phrase, with AI keyword suggestions, rank history and a neighbourhood grid map." },
      { icon: Swords, title: "Competitor Analysis", desc: "Head-to-head on rating, reviews, photos, hours and completeness, a 'where they beat you' list with a fix for each gap, exportable as PDF or CSV." },
      { icon: BarChart3, title: "Insights & Search Keywords", desc: "Calls, website clicks, direction requests and impressions over 7, 30 or 90 days - plus the actual phrases customers typed to find you." },
    ],
  },
  {
    id: "facebook",
    slug: "facebook-page-automation-white-label-software-for-agencies",
    keyword: "Facebook automation",
    seoTitle: "White-Label Facebook Automation Software for Agencies",
    seoDescription:
      "Publish, schedule and reply to Facebook comments and Messenger for every client Page from one dashboard - white-label software your agency resells.",
    group: "automation",
    tag: "Facebook",
    title: "Facebook Automation",
    image: "/facebook-meta-automation-magicbolt.png",
    cardImage: "/facebook-automation.webp",
    intro:
      "Run every Facebook Page from one place - publish, schedule, and answer comments and Messenger without switching tabs.",
    accent: { Icon: FaFacebookF, color: "#1877F2", tint: "rgba(24,119,242,0.10)" },
    points: [
      { icon: Share2, title: "Publish & Schedule", desc: "Post images and videos to your Facebook Page, Instagram, or both at once - now or on a schedule." },
      { icon: MessageCircle, title: "Comments Inbox", desc: "Comments from every post in one place, filterable by date, platform and 'needs reply'." },
      { icon: MessagesSquare, title: "Unified Messenger Inbox", desc: "Messenger and Instagram DMs together in a single inbox, so no customer message slips through." },
      { icon: Gauge, title: "Overview & Insights", desc: "Followers, reach, response-rate score, required actions and a publishing queue - with multiple Pages per business." },
    ],
  },
  {
    id: "instagram",
    slug: "instagram-automation-white-label-software-for-agencies",
    keyword: "Instagram automation",
    seoTitle: "White-Label Instagram Automation Software for Agencies",
    seoDescription:
      "Schedule Reels and posts, answer every DM and mention, and track Instagram performance for all clients - white-label Instagram automation software for agencies.",
    group: "automation",
    tag: "Instagram",
    title: "Instagram Automation",
    image: "/instagrame-automation-magicbolt.png",
    cardImage: "/instagram-automation.webp",
    intro:
      "Schedule Reels and posts, reply to every DM and mention, and track performance side by side with Facebook.",
    accent: { Icon: FaInstagram, color: "#fff", gradient: "linear-gradient(135deg,#F58529,#DD2A7B,#8134AF)" },
    points: [
      { icon: CalendarDays, title: "Reel & Post Scheduling", desc: "Publish or schedule images, videos and Instagram Reels, with a live phone preview before it goes out." },
      { icon: AtSign, title: "Mentions & Story Tags", desc: "See every Instagram tag and story mention in one feed, and reply to the customer by DM." },
      { icon: Inbox, title: "DM Inbox", desc: "Instagram Direct Messages sit alongside Messenger in one unified inbox." },
      { icon: BarChart3, title: "Instagram Insights", desc: "Instagram and Facebook Page performance side by side." },
    ],
  },
  {
    id: "whatsapp",
    slug: "whatsapp-api-crm-automation-white-label-software-for-agencies",
    keyword: "WhatsApp API CRM automation",
    seoTitle: "White-Label WhatsApp API CRM Software for Agencies",
    seoDescription:
      "Bulk messages, broadcasts, 24/7 auto replies and a chatbot that captures leads and books appointments - white-label WhatsApp API CRM software for agencies.",
    group: "automation",
    tag: "WhatsApp",
    title: "WhatsApp API CRM",
    image: "/whatsapp-automation-magicbolt.png",
    cardImage: "/whatsapp-crm.webp",
    intro:
      "Turn WhatsApp chats into customers - bulk messages, broadcast campaigns, auto replies and a chatbot that captures leads around the clock.",
    accent: { Icon: FaWhatsapp, color: "#25D366", tint: "rgba(37,211,102,0.12)" },
    points: [
      { icon: Send, title: "Bulk Messages", desc: "Send one message to many customers at once on WhatsApp instead of typing it out chat by chat." },
      { icon: Megaphone, title: "Broadcast Campaigns", desc: "Run offers, announcements and follow-up campaigns to your contact lists." },
      { icon: MessageCircle, title: "Auto Replies", desc: "Reply to incoming chats automatically, 24/7, so customers get an answer even when the team is offline." },
      { icon: Bot, title: "Chatbot & Lead Capture", desc: "A chatbot shares services and pricing from menu buttons and captures every enquiry as a lead." },
      { icon: CalendarCheck, title: "Appointment Booking", desc: "Customers book a demo or appointment inside the chat and get a confirmation message." },
      { icon: BarChart3, title: "Track & Grow", desc: "Track how messages and campaigns perform, and turn more chats into customers." },
    ],
  },
  {
    id: "ai-calling",
    slug: "ai-calling-voice-agent-white-label-software-for-agencies",
    keyword: "AI calling voice agent",
    seoTitle: "White-Label AI Calling & Voice Agent Software for Agencies",
    seoDescription:
      "AI voice agents that answer and make phone calls, qualify leads, book appointments and log every call - white-label AI calling software for agencies.",
    group: "automation",
    tag: "AI Calling",
    title: "AI Calling",
    image: "/ai-calling.webp",
    cardImage: "/ai-calling.webp",
    intro:
      "An AI voice agent that picks up every call, talks to customers in natural language, qualifies leads and books appointments - day and night.",
    accent: { Icon: Phone, color: "#7C3AED", tint: "rgba(124,58,237,0.10)" },
    points: [
      { icon: PhoneIncoming, title: "Answers Every Call", desc: "An AI voice agent picks up inbound calls 24/7, so no enquiry goes to voicemail after hours." },
      { icon: PhoneOutgoing, title: "Outbound Calling", desc: "Call leads and customers automatically for follow-ups, reminders and campaigns." },
      { icon: Bot, title: "Natural Conversations", desc: "Talks in a natural voice, answers common questions about the business and hands over when a person is needed." },
      { icon: CalendarCheck, title: "Qualify & Book", desc: "Collects the caller's details, qualifies the lead and books an appointment on the call." },
      { icon: FileText, title: "Call Logs & Transcripts", desc: "Every call is recorded with a transcript and summary, so nothing is lost." },
    ],
  },
  {
    id: "aeo-geo",
    slug: "aeo-geo-ai-search-optimization-white-label-software-for-agencies",
    keyword: "AEO + GEO AI search optimization",
    seoTitle: "White-Label AEO & GEO AI Search Software for Agencies",
    seoDescription:
      "Check whether ChatGPT, Gemini and Perplexity recommend a business, then fix the gaps - white-label AEO and GEO optimization software for agencies.",
    group: "automation",
    tag: "AI Search",
    title: "AEO + GEO Visibility",
    image: "/aeo-geo-automation-magicbolt.png",
    cardImage: "/aeo-geo.webp",
    intro:
      "Find out whether ChatGPT, Gemini and Perplexity can find and recommend a business - then fix what's stopping them.",
    accent: { Icon: Search, color: "#16A34A", tint: "rgba(22,163,74,0.10)" },
    points: [
      { icon: Bot, title: "AI-Readiness Scan", desc: "Scan any website - yours or a competitor's - for an AI-readiness score, AI bot crawl-access check and per-engine results." },
      { icon: ListChecks, title: "Fix-It Task List", desc: "Recommendations become a tickable task list, with ready-made files to fix each issue." },
      { icon: TrendingUp, title: "Score History & Comparison", desc: "Track score changes over time and compare against other sites, with your own site pinned first." },
      { icon: FileText, title: "Google Profile Report - Any Business", desc: "Run a report on any business on Google Maps with no connection needed: competitors ranking above, issues found, completion %, a 5x5 rank grid and a PDF." },
    ],
  },
  {
    id: "review-routing",
    slug: "review-booster-qr-google-reviews-automation-white-label-software-for-agencies",
    keyword: "Review booster QR & Google reviews automation",
    seoTitle: "White-Label Review Booster & QR Review Software for Agencies",
    seoDescription:
      "Send happy customers to Google reviews and capture unhappy feedback privately with a QR code or link - white-label review software agencies resell.",
    group: "software",
    tag: "Reputation",
    title: "Review Booster QR",
    image: "/mutiplateform-review-automation-magicbolt.png",
    cardImage: "/review-booster.webp",
    intro:
      "A QR code or link that sends happy customers to Google and captures unhappy feedback privately first.",
    accent: { Icon: ShieldCheck, color: "#F59E0B", tint: "rgba(245,158,11,0.12)" },
    points: [
      { icon: Filter, title: "Smart Sentiment Routing", desc: "Customers rate first: happy ones go straight to your Google review page, unhappy ones reach you privately so you can fix it." },
      { icon: QrCode, title: "Printable QR Standees", desc: "Ready-made standee designs to print and place on counters and tables." },
      { icon: BarChart3, title: "Page Analytics", desc: "Views, visitors and conversions for every Review Booster page." },
    ],
    // Business-opportunity block with a vertical (9:16) video, shown on this
    // feature's detail page (FeatureDetailPage -> BusinessPromo).
    promo: {
      videoId: "wmtDr_H0TX0",
      badge: "Business Opportunity",
      titleLead: "Start Your Own",
      titleAccent: "White-Label Review Booster Business",
      titleTail: "with MagicBolt AI",
      description:
        "Launch your own branded review management business with MagicBolt AI. Help local businesses improve their online reputation while building a recurring-revenue business under your own brand.",
      benefits: [
        "Your Own Brand & White-Label Platform",
        "Smart QR Review Collection Software",
        "Google Business Profile & Reputation Solutions",
        "Sell to Local Businesses & Build Recurring Revenue",
      ],
      cta: "Start Your Business",
      note: "Powered by MagicBolt AI · Built for Agencies & Entrepreneurs",
    },
  },
  {
    id: "qr-nfc",
    slug: "nfc-smart-digital-business-card-white-label-software-for-agencies",
    keyword: "NFC smart digital business card",
    seoTitle: "White-Label NFC Digital Business Card Software for Agencies",
    seoDescription:
      "Create QR and NFC digital visiting cards with contact details, socials and links in one tap - white-label business card software for your agency.",
    group: "software",
    tag: "Smart Card",
    title: "Smart Digital Card",
    image: "/nfccard-business-automation-magicbolt.png",
    cardImage: "/nfc-smart-card.webp",
    intro:
      "A digital business card with its own QR code and shareable link - contact details, socials and more in one tap.",
    accent: { Icon: QrCode, color: "#FF6B00", tint: "rgba(255,107,0,0.10)" },
    points: [
      { icon: Contact, title: "Digital Business Card", desc: "Share contact details, social links and your review page from a single card." },
      { icon: Palette, title: "Themes & Custom Domains", desc: "Pick from the theme catalogue and serve the card on your own domain." },
      { icon: BarChart3, title: "Card Analytics", desc: "See views, visitors and conversions from every scan and share." },
    ],
  },
  {
    id: "bio-links",
    slug: "link-in-bio-instant-website-builder-white-label-software-for-agencies",
    keyword: "Link-in-bio & instant website builder",
    seoTitle: "White-Label Link-in-Bio & Website Builder for Agencies",
    seoDescription:
      "Give every client a link-in-bio page and a one-page website built automatically from their channels - white-label website builder software for agencies.",
    group: "software",
    tag: "Links & Website",
    title: "Links Page + Instant Website",
    image: "/biolink-visitingcard-automation-magicbolt.png",
    cardImage: "/bio-link.webp",
    intro:
      "A link-in-bio page for every social profile, plus a one-page website built automatically from your connected channels.",
    accent: { Icon: Link2, color: "#8B5CF6", tint: "rgba(139,92,246,0.10)" },
    points: [
      { icon: LayoutList, title: "Link-in-Bio Page", desc: "One link that routes followers to your website, WhatsApp, menu, socials or offers." },
      { icon: Palette, title: "Branded Themes", desc: "Choose from the theme catalogue and serve pages on a custom domain." },
      { icon: BarChart3, title: "Page Analytics", desc: "Views, visitors and conversions, so you know what's working." },
    ],
  },
  {
    id: "menu",
    slug: "qr-digital-menu-for-restaurants-white-label-software-for-agencies",
    keyword: "QR digital menu for restaurants",
    seoTitle: "White-Label QR Digital Menu Software for Agencies",
    seoDescription:
      "QR menus for restaurants and cafes with categories, items, prices and photos updated in seconds - white-label digital menu software agencies resell.",
    group: "software",
    tag: "Hospitality",
    title: "Smart Digital Menu",
    image: "/digital-menu-automation-magicbolt.png",
    cardImage: "/smart-menu.webp",
    intro:
      "A QR menu for restaurants and cafes - categories, items, prices and photos, updated in seconds.",
    accent: { Icon: UtensilsCrossed, color: "#EF4444", tint: "rgba(239,68,68,0.10)" },
    points: [
      { icon: RefreshCw, title: "Instant Menu Updates", desc: "Edit categories, items and prices any time - the QR menu updates with no reprinting." },
      { icon: ImagePlus, title: "Photos & Themes", desc: "Add item photos and pick a theme that matches the restaurant's brand." },
      { icon: BarChart3, title: "Menu Analytics", desc: "See how many guests view the menu and when." },
    ],
  },
  {
    id: "wordpress",
    slug: "wordpress-auto-publishing-automation-white-label-software-for-agencies",
    keyword: "WordPress auto-publishing automation",
    seoTitle: "White-Label WordPress Automation Software for Agencies",
    seoDescription:
      "Publish and schedule WordPress blog posts from the same composer as social and Google - white-label WordPress automation software for agencies.",
    group: "automation",
    tag: "WordPress",
    title: "WordPress Publishing",
    image: "/woordpress-automation-magicbolt.png",
    cardImage: "/wordpress-automation.webp",
    intro:
      "Publish and schedule blog posts to your WordPress site from the same composer you use for social and Google.",
    accent: { Icon: FaWordpress, color: "#21759B", tint: "rgba(33,117,155,0.10)" },
    points: [
      { icon: FileText, title: "Publish & Schedule Posts", desc: "Write once and send blog posts to WordPress now or on a schedule." },
      { icon: LayoutList, title: "Manage Existing Content", desc: "Browse and manage the posts already on your site." },
      { icon: MessageCircle, title: "Scheduled Comments", desc: "Queue comments on your posts to go out at the right time." },
    ],
  },
  {
    id: "appointments",
    slug: "appointment-scheduling-booking-white-label-software-for-agencies",
    keyword: "Appointment scheduling & booking",
    seoTitle: "White-Label Appointment Booking Software for Agencies",
    seoDescription:
      "Online booking, automatic WhatsApp and email reminders, deposits and calendar sync that cut no-shows - white-label appointment software for agencies.",
    group: "software",
    tag: "Bookings",
    title: "Appointment Scheduling",
    image: "/appointment-scheduling-automation-magicbolt.png",
    cardImage: "/appointment-scheduling.webp",
    intro:
      "Let customers book online 24/7, then send reminders, take deposits and sync every calendar automatically - fewer no-shows, more bookings.",
    accent: { Icon: CalendarCheck, color: "#2563EB", tint: "rgba(37,99,235,0.10)" },
    points: [
      { icon: CalendarCheck, title: "Online Booking", desc: "A branded booking page where customers pick a service, date and open time slot and book in a few taps." },
      { icon: Users, title: "Manage Appointments", desc: "Every booking, reschedule and cancellation in one dashboard, with staff and service assignments." },
      { icon: BellRing, title: "Auto Reminders", desc: "Booking confirmations and appointment reminders sent automatically on WhatsApp and email to cut no-shows." },
      { icon: CreditCard, title: "Payments & Deposits", desc: "Collect full payment or a deposit at the time of booking so customers turn up." },
      { icon: RefreshCw, title: "Calendar Sync", desc: "Keep bookings in sync with the calendars you already use, with add-to-calendar for customers." },
      { icon: Video, title: "Call, Video or In-Person", desc: "Offer phone, video-meeting or at-location appointments, with the details sent to the customer." },
      { icon: BarChart3, title: "Reports & Insights", desc: "Track bookings, no-shows and busiest slots to fill the calendar and grow the business." },
    ],
  },
  {
    id: "agency",
    platform: true,
    // The agency page also lists these features in full (FeatureDetailPage).
    includes: ["publishing", "ai-tools"],
    slug: "agency-os-automation-platform-white-label-software-for-agencies",
    keyword: "Agency automation platform",
    seoTitle: "White-Label Agency Automation Platform to Resell as SaaS",
    seoDescription:
      "Resell the whole MagicBolt AI platform under your brand and domain - clients, plans, usage limits, billing and outreach in one white-label platform.",
    group: "software",
    tag: "For Agencies",
    title: "White-Label Agency Platform",
    intro:
      "Resell the whole platform under your own brand - clients, plans, billing, usage limits and outreach in one agency menu.",
    accent: { Icon: Building2, color: "#0B1533", tint: "rgba(11,21,51,0.08)" },
    cardImage: "/white-label-agency-platform.webp",
    points: [
      { icon: Palette, title: "Full White-Label", desc: "Your brand name, logo, favicon, colours and login page, on your own domain for sign-ups and a separate domain for QR pages." },
      { icon: Users, title: "Client Management", desc: "Create clients, log in to their panel in a new tab with no password, allot plans and message one or many at once." },
      { icon: SlidersHorizontal, title: "Plans & Usage Limits", desc: "Build unlimited pricing tiers and set monthly caps on Profile Reports, AI search scans and competitor analyses, so API costs never run away." },
      { icon: Receipt, title: "Billing & Revenue", desc: "Recurring invoices, part payments, and MRR, ARPU and churn at a glance." },
      { icon: Search, title: "Lead Center", desc: "Find prospect businesses on Google Maps with an optimisation score for each, and sell to the weakest profiles." },
      { icon: Mail, title: "Bulk Mailing", desc: "Announcements and drip campaigns through your own SMTP, with templates and delivery tracking." },
      { icon: KeyRound, title: "Bring Your Own Keys", desc: "Use your own Google Maps, AI provider, SMTP and storage accounts so usage bills to you directly." },
    ],
  },
  {
    id: "publishing",
    platform: true,
    slug: "social-media-scheduler-content-calendar-white-label-software-for-agencies",
    keyword: "Social media scheduler & content calendar",
    seoTitle: "White-Label Social Media Scheduler & Calendar for Agencies",
    seoDescription:
      "Write once and publish to Google, Facebook, Instagram and WordPress, with every client's posts on one calendar - white-label scheduling for agencies.",
    group: "software",
    tag: "Publishing",
    title: "Composer & Content Calendar",
    intro:
      "Write once and post to Google, Facebook, Instagram and WordPress together - then see every post on one calendar.",
    accent: { Icon: CalendarRange, color: "#F97316", tint: "rgba(249,115,22,0.10)" },
    cardImage: "/social-planner.webp",
    points: [
      { icon: Layers, title: "One Composer, Every Channel", desc: "A live phone preview for each platform. Post now, schedule, or save as draft." },
      { icon: MessageCircle, title: "Scheduled First Comments", desc: "Auto-post a first comment and threaded replies minutes or hours after the post goes live." },
      { icon: CalendarDays, title: "Content Calendar", desc: "Month or list view of every post across all channels, colour-coded as draft, scheduled, published or failed." },
      { icon: Images, title: "Bulk Media Scheduler", desc: "Upload many photos or videos and spread them out daily, on alternate days or on a custom schedule." },
      { icon: Megaphone, title: "Suggested Posts & Media Library", desc: "Monthly festival and promotional templates, plus a drag-and-drop library to upload once and reuse everywhere." },
    ],
  },
  {
    id: "ai-tools",
    platform: true,
    slug: "ai-agent-autopilot-marketing-automation-white-label-software-for-agencies",
    keyword: "AI agent & autopilot marketing automation",
    seoTitle: "White-Label AI Marketing Agent & Autopilot for Agencies",
    seoDescription:
      "Train an AI on each client's brand voice, then let it write, schedule and draft content on autopilot - white-label AI marketing software for agencies.",
    group: "software",
    tag: "AI",
    title: "AI Agent & Autopilot",
    intro:
      "Teach the AI your brand voice once - then let it write, schedule and draft on-brand content for every channel.",
    accent: { Icon: Sparkles, color: "#A855F7", tint: "rgba(168,85,247,0.10)" },
    cardImage: "/ai-agent-autopilot.webp",
    points: [
      { icon: Bot, title: "Brand Voice Agent", desc: "Teach the AI your tone, keywords and style once, and everything it writes stays on-brand." },
      { icon: RefreshCw, title: "AI Autopilot", desc: "The AI creates new posts daily, weekly or monthly - held as drafts for your approval or published automatically." },
      { icon: Sparkles, title: "AI Posts & Captions", desc: "One-click captions in the composer, or generate a batch of posts for all channels to review and approve." },
      { icon: MessagesSquare, title: "AI Assistant", desc: "A chat copilot and marketing agent that knows your business." },
      { icon: Plug, title: "MCP Connector", desc: "Manage your business from Claude, ChatGPT or Cursor - read and draft only, with a person approving anything before it goes live." },
    ],
  },
];

// Standalone detail page for each feature, e.g.
// /features/google-business-profile-automation-white-label-software-for-agencies
export const featurePath = (cat) => `/features/${cat.slug}`;
export const getFeatureBySlug = (slug) => FEATURE_CATEGORIES.find((c) => c.slug === slug);

// Channel/content automation - the "Agency OS" core. Everything else is
// bundled software an agency can resell alongside it.
// Lightweight WebP copy of a feature's `image`, for on-page display. The PNG
// in `image` stays as the social-preview / schema image, which need PNG.
export const displayImage = (cat) => cat.image && cat.image.replace(/\.png$/, ".webp");

export const AUTOMATION_CATEGORIES = FEATURE_CATEGORIES.filter((c) => c.group === "automation");
export const SOFTWARE_CATEGORIES = FEATURE_CATEGORIES.filter((c) => c.group === "software");

// Agency platform tools - shown as their own card group on the features page.
const PLATFORM_ORDER = ["agency", "publishing", "ai-tools"];
export const PLATFORM_CATEGORIES = PLATFORM_ORDER.map((id) => FEATURE_CATEGORIES.find((c) => c.id === id));
